/**
 * RunFailureHeadline — the "what failed" headline shown at the top of a
 * failed run's summary surface.
 *
 * Shared by the flow-designer run panel (action: "View details" → failure
 * drawer) and the full-page run detail (action: "Diagnose/Jump to root
 * cause"), so the failed-step derivation and the headline copy can't drift
 * between the two views.
 *
 * Two shapes, matching how runs fail:
 *  - a failed step → "Step failed: <node>" + the step's error sentence
 *  - no failed step (timeout / infra) → "Run failed" + the run-level error
 *
 * Deliberately subtle: a light destructive tint and a ghost text-arrow
 * action, not a heavy alert card — the step list below carries the detail.
 *
 * @module ui/pages/FlowDesigner/runs/RunFailureHeadline
 */
import React from 'react';
import { ArrowRight, Warning } from '@phosphor-icons/react';
import { Flex, Text } from '@chatsdet/ui';
import { API_URL } from '../../../../config';
import type { WorkflowRunStepDto } from '../hooks/useWorkflowRun';
import type { WorkflowNode } from '../../../../types/workflow';
import { isWarnAssertionStep, nodeLabelWithStepsFallback } from './RunStepList';

/** Failure context provided by the agent. */
export interface FailureContext {
  observedBehavior?: string;
  expectedBehavior?: string;
  reproSteps?: string;
  questions?: string[];
}

/** Data describing the step that failed a run. */
export interface FailedStepData {
  nodeId: string;
  nodeName: string;
  nodeType: string;
  errorMessage: string | null;
  failureScreenshotUrl?: string | null;
  failureContext?: FailureContext | null;
  /** Assumptions the agent made during execution (debug mode). */
  assumptions?: string[] | null;
}

/** Convert S3 key to API URL for screenshots */
const getScreenshotUrl = (s3Key: string): string => {
  // Rendered into `<img src>` which can't send the org header — carry the org
  // as a query param.
  return `${API_URL}/screenshots/${encodeURIComponent(s3Key)}`;
};

/** The step that failed the run, excluding non-blocking warn assertions. */
export const findFailedRunStep = (steps: WorkflowRunStepDto[]): WorkflowRunStepDto | undefined =>
  steps.find((s) => s.status === 'failed' && !isWarnAssertionStep(s));

/** Derive FailedStepData from a workflow step and nodes */
export const getFailedStepData = (
  step: WorkflowRunStepDto,
  nodes: WorkflowNode[],
  allSteps?: WorkflowRunStepDto[]
): FailedStepData => {
  const failureScreenshotS3Key = step.metadataJson?.failureScreenshotS3Key;

  return {
    nodeId: step.nodeId,
    nodeName: nodeLabelWithStepsFallback(nodes, step.nodeId, allSteps),
    nodeType:
      nodes.find((n) => n.id === step.nodeId)?.type ??
      (step.metadataJson?.nodeType as string) ??
      'unknown',
    errorMessage: step.errorMessage,
    failureScreenshotUrl: failureScreenshotS3Key ? getScreenshotUrl(failureScreenshotS3Key) : null,
    failureContext: step.metadataJson?.failureContext ?? null,
    assumptions: (step.metadataJson?.assumptions as string[] | undefined) ?? null,
  };
};

export interface FailureHeadlineAction {
  label: string;
  onPress: () => void;
  isDisabled?: boolean;
}

export function RunFailureHeadline({
  failedStep,
  runErrorMessage,
  action,
}: {
  failedStep: FailedStepData | null;
  /** Run-level error, shown when the run failed without a failed step. */
  runErrorMessage?: string | null;
  /** Optional ghost text→arrow action rendered under the message. */
  action?: FailureHeadlineAction;
}) {
  if (!failedStep && !runErrorMessage) return null;

  return (
    <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
      <Flex align="start" gap="sm">
        <Warning size={14} weight="fill" className="text-destructive mt-0.5 shrink-0" />
        <div className="min-w-0 flex-1">
          <Text
            size="sm"
            weight="medium"
            className="line-clamp-2 text-destructive"
            title={failedStep ? `Step failed: ${failedStep.nodeName}` : 'Run failed'}
          >
            {failedStep ? `Step failed: ${failedStep.nodeName}` : 'Run failed'}
          </Text>
          {(failedStep ? failedStep.errorMessage : runErrorMessage) && (
            <Text
              size="xs"
              className="mt-0.5 line-clamp-2 text-muted-foreground"
              title={
                failedStep ? (failedStep.errorMessage ?? undefined) : (runErrorMessage ?? undefined)
              }
            >
              {failedStep ? failedStep.errorMessage : runErrorMessage}
            </Text>
          )}
          {action && (
            <button
              type="button"
              disabled={action.isDisabled}
              onClick={action.onPress}
              className="mt-1.5 -ml-1 inline-flex items-center gap-1 rounded px-1 py-0.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {action.label}
              <ArrowRight size={12} />
            </button>
          )}
        </div>
      </Flex>
    </div>
  );
}
